import type { TranscriptEntry } from './SearchTranscript.types';

interface FRSPhaseLogProps {
  entries: TranscriptEntry[];
  expanded: Set<string>;
  onToggle: (phase: string) => void;
  formatTime: (ts: string) => string;
  isRunning: boolean;
}

const MAX_DETAIL_ROWS = 50;

export default function FRSPhaseLog({ entries, expanded, onToggle, formatTime, isRunning }: FRSPhaseLogProps) {
  const preFilter    = entries.filter(e => e.function_name === 'pre_filter');
  const pass1Header  = entries.filter(e => e.function_name === 'pass1_batch_a');
  const assessments  = entries.filter(e => e.function_name === 'relevance_assessment');
  const enrichments  = entries.filter(e => e.function_name === 'enrichment');
  const summaryEnt   = entries.filter(e => e.function_name === 'summary');

  const aboveThreshold = assessments.filter(e => e.outputs?.['above_threshold'] === true).length;
  const expectedTotal  = (pass1Header[0]?.inputs?.['doc_count'] as number | undefined) ?? assessments.length;

  const msToHuman = (ms: number) => {
    if (ms < 1000) return `${Math.round(ms)}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${Math.floor(ms / 60000)}m ${Math.round((ms % 60000) / 1000)}s`;
  };

  const rangeDuration = (list: TranscriptEntry[]) => {
    if (list.length === 0) return null;
    const start = new Date(list[0].timestamp).getTime();
    const end   = new Date(list[list.length - 1].timestamp).getTime();
    return end > start ? msToHuman(end - start) : null;
  };

  const pass1Duration   = rangeDuration([...(pass1Header.length ? [pass1Header[0]] : []), ...assessments]);
  const pass2Duration   = rangeDuration(enrichments);
  const lastSotaChars   = enrichments.length > 0 ? (enrichments[enrichments.length - 1].outputs?.['sota_length_chars'] as number | undefined) : undefined;
  const summaryDuration = summaryEnt[0]?.duration_ms != null ? msToHuman(summaryEnt[0].duration_ms) : null;

  const scoreOf = (e: TranscriptEntry) => (e.outputs?.['relevance_percent'] as number | undefined) ?? 0;

  return (
    <div className="frs-phase-log">

      {/* Pre-filter */}
      {preFilter.length > 0 && (
        <div className="fpl-card fpl-card-muted">
          <div className="fpl-card-header">
            <span className="fpl-step-label">Pre-filter</span>
            <span className="fpl-card-desc">
              {preFilter[0]?.description ?? `${(preFilter[0]?.outputs?.['count'] as number | undefined) ?? preFilter.length} document(s) skipped — no readable text`}
            </span>
          </div>
        </div>
      )}

      {/* Pass 1 — Relevance screening */}
      <div className="fpl-card">
        <div className="fpl-card-header" onClick={() => onToggle('pass1')} style={{ cursor: 'pointer' }}>
          <div className="fpl-card-title-row">
            <span className="fpl-step-label">Pass 1</span>
            <span className="fpl-card-title">Relevance screening</span>
            {isRunning && assessments.length < expectedTotal && (
              <span className="fpl-running-badge">in progress</span>
            )}
            {assessments.length > 0 && (
              <button className="fpl-expand-btn" type="button">
                {expanded.has('pass1') ? 'Hide details' : `Show ${assessments.length} entries`}
              </button>
            )}
          </div>
          <div className="fpl-card-stats">
            <span><strong>{assessments.length}</strong> {isRunning && assessments.length < expectedTotal ? `/ ${expectedTotal.toLocaleString()}` : ''} assessed</span>
            <span className="fpl-stat-sep">·</span>
            <span className="fpl-stat-relevant"><strong>{aboveThreshold}</strong> relevant</span>
            <span className="fpl-stat-sep">·</span>
            <span className="fpl-stat-below"><strong>{assessments.length - aboveThreshold}</strong> below threshold</span>
            {pass1Duration && <><span className="fpl-stat-sep">·</span><span>{pass1Duration}</span></>}
          </div>
          {isRunning && assessments.length < expectedTotal && expectedTotal > 0 && (
            <div className="fpl-progress-bar-wrap">
              <div className="fpl-progress-bar" style={{ width: `${Math.round((assessments.length / expectedTotal) * 100)}%` }} />
            </div>
          )}
        </div>
        {expanded.has('pass1') && assessments.length > 0 && (
          <div className="fpl-details">
            {assessments.length > MAX_DETAIL_ROWS && (
              <div className="fpl-truncation-note">Showing first {MAX_DETAIL_ROWS} of {assessments.length.toLocaleString()} entries</div>
            )}
            <table className="fpl-detail-table">
              <thead>
                <tr><th>Time</th><th>Document</th><th className="fpl-th-score">Score</th><th>Rationale</th></tr>
              </thead>
              <tbody>
                {assessments.slice(0, MAX_DETAIL_ROWS).map((e, i) => {
                  const pct = scoreOf(e);
                  const above = e.outputs?.['above_threshold'] === true;
                  return (
                    <tr key={i} className={above ? 'fpl-row-relevant' : 'fpl-row-below'}>
                      <td className="fpl-td-time">{formatTime(e.timestamp)}</td>
                      <td className="fpl-td-doc">{String(e.inputs?.['document'] ?? '')}</td>
                      <td className="fpl-td-score">
                        <span className="fpl-score-badge" style={{
                          background: pct >= 51 ? '#d1fae5' : pct >= 31 ? '#fef9c3' : '#f3f4f6',
                          color:      pct >= 51 ? '#065f46' : pct >= 31 ? '#713f12' : '#6b7280',
                        }}>{pct}%</span>
                      </td>
                      <td className="fpl-td-rationale">{String(e.description ?? '').replace(/^[^:]+:\s*\d+%\s*—\s*/, '')}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pass 2 — SotA enrichment */}
      {(enrichments.length > 0 || (!isRunning && aboveThreshold > 0)) && (
        <div className="fpl-card">
          <div className="fpl-card-header" onClick={() => onToggle('pass2')} style={{ cursor: 'pointer' }}>
            <div className="fpl-card-title-row">
              <span className="fpl-step-label">Pass 2</span>
              <span className="fpl-card-title">State-of-the-art enrichment</span>
              {isRunning && enrichments.length < aboveThreshold && enrichments.length > 0 && (
                <span className="fpl-running-badge">in progress</span>
              )}
              {enrichments.length > 0 && (
                <button className="fpl-expand-btn" type="button">
                  {expanded.has('pass2') ? 'Hide details' : `Show ${enrichments.length} entries`}
                </button>
              )}
            </div>
            <div className="fpl-card-stats">
              <span><strong>{enrichments.length}</strong> relevant documents used to enrich SotA document</span>
              {lastSotaChars != null && <><span className="fpl-stat-sep">·</span><span>SotA: {lastSotaChars.toLocaleString()} chars</span></>}
              {pass2Duration && <><span className="fpl-stat-sep">·</span><span>{pass2Duration}</span></>}
            </div>
          </div>
          {expanded.has('pass2') && enrichments.length > 0 && (
            <div className="fpl-details">
              {enrichments.length > MAX_DETAIL_ROWS && (
                <div className="fpl-truncation-note">Showing first {MAX_DETAIL_ROWS} of {enrichments.length.toLocaleString()} entries</div>
              )}
              <table className="fpl-detail-table">
                <thead>
                  <tr><th>Time</th><th>Document</th><th className="fpl-th-score">Relevance</th><th>SotA size</th></tr>
                </thead>
                <tbody>
                  {enrichments.slice(0, MAX_DETAIL_ROWS).map((e, i) => (
                    <tr key={i}>
                      <td className="fpl-td-time">{formatTime(e.timestamp)}</td>
                      <td className="fpl-td-doc">{String(e.inputs?.['document'] ?? '')}</td>
                      <td className="fpl-td-score">
                        <span className="fpl-score-badge" style={{ background: '#d1fae5', color: '#065f46' }}>
                          {e.inputs?.['relevance_percent'] as number ?? '—'}%
                        </span>
                      </td>
                      <td className="fpl-td-rationale">{((e.outputs?.['sota_length_chars'] as number | undefined) ?? 0).toLocaleString()} chars</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Pass 3 — Summary generation */}
      {(summaryEnt.length > 0 || (!isRunning && enrichments.length > 0)) && (
        <div className="fpl-card">
          <div className="fpl-card-header">
            <div className="fpl-card-title-row">
              <span className="fpl-step-label">Pass 3</span>
              <span className="fpl-card-title">Executive summary generation</span>
              {isRunning && summaryEnt.length === 0 && (
                <span className="fpl-running-badge">in progress</span>
              )}
            </div>
            {summaryEnt.length > 0 && (
              <div className="fpl-card-stats">
                <span>Summary generated from <strong>{summaryEnt[0]?.inputs?.['relevant_docs'] as number ?? enrichments.length}</strong> relevant documents</span>
                {(summaryEnt[0]?.outputs?.['summary_length'] as number | undefined) != null && (
                  <><span className="fpl-stat-sep">·</span><span>{(summaryEnt[0].outputs['summary_length'] as number).toLocaleString()} chars</span></>
                )}
                {summaryDuration && <><span className="fpl-stat-sep">·</span><span>{summaryDuration}</span></>}
              </div>
            )}
          </div>
        </div>
      )}

    </div>
  );
}
